import axios from "axios"
import { Dispatch } from "redux"
import { AppDispatch } from "../store.config"

const enum PaymentMethodsActionsEnum {
  GET_PAYMENT_METHODS_ATTEMPT = "PAYMENT_METHODS/getPaymentMethodsAttempt",
  GET_PAYMENT_METHODS_SUCCESS = "PAYMENT_METHODS/getPaymentMethodsSuccess",
  GET_PAYMENT_METHODS_FAILURE = "PAYMENT_METHODS/getPaymentMethodsFailure",
  CREATE_PAYMENT_METHODS_ATTEMPT = "PAYMENT_METHODS/createPaymentMethodsAttempt",
  CREATE_PAYMENT_METHODS_SUCCESS = "PAYMENT_METHODS/createPaymentMethodsSuccess",
  CREATE_PAYMENT_METHODS_FAILURE = "PAYMENT_METHODS/createPaymentMethodsFailure",
  DELETE_PAYMENT_METHODS_ATTEMPT = "PAYMENT_METHODS/deletePaymentMethodsAttempt",
  DELETE_PAYMENT_METHODS_SUCCESS = "PAYMENT_METHODS/deletePaymentMethodsSuccess",
  DELETE_PAYMENT_METHODS_FAILURE = "PAYMENT_METHODS/deletePaymentMethodsFailure",
}

export const getPaymentMethodsAttemptAction = () =>
  ({ type: PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_ATTEMPT } as const)

export const getPaymentMethodsSuccessAction = (
  paymentMethods: Array<PaymentMethod>
) =>
  ({
    type: PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_SUCCESS,
    payload: paymentMethods,
  } as const)

export const getPaymentMethodsFailureAction = (error: Error) =>
  ({ type: PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_FAILURE } as const)

export const createPaymentMethodsAttemptAction = () =>
  ({ type: PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_ATTEMPT } as const)

export const createPaymentMethodsSuccessAction = () =>
  ({
    type: PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_SUCCESS,
  } as const)

export const createPaymentMethodsFailureAction = (error: Error) =>
  ({ type: PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_FAILURE } as const)

export const deletePaymentMethodsAttemptAction = () =>
  ({ type: PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_ATTEMPT } as const)

export const deletePaymentMethodsSuccessAction = () =>
  ({ type: PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_SUCCESS } as const)

export const deletePaymentMethodsFailureAction = (error: Error) =>
  ({ type: PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_FAILURE } as const)

type PaymentMethodsActionsType = ReturnType<
  | typeof getPaymentMethodsAttemptAction
  | typeof getPaymentMethodsSuccessAction
  | typeof getPaymentMethodsFailureAction
  | typeof createPaymentMethodsAttemptAction
  | typeof createPaymentMethodsSuccessAction
  | typeof createPaymentMethodsFailureAction
  | typeof deletePaymentMethodsAttemptAction
  | typeof deletePaymentMethodsSuccessAction
  | typeof deletePaymentMethodsFailureAction
>

export const paymentMethodsInitialState: PaymentMethodsState = {
  paymentMethods: [],
  createPaymentMethodStatus: "DEFAULT",
}

export interface PaymentMethodsState {
  paymentMethods: Array<PaymentMethod>
  createPaymentMethodStatus: "DEFAULT" | "LOADING" | "SUCCESS" | "FAILURE"
}

export function paymentMethodsReducer(
  state = paymentMethodsInitialState,
  action: PaymentMethodsActionsType
): PaymentMethodsState {
  switch (action.type) {
    case PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_ATTEMPT:
      return { ...state, paymentMethods: [] }
    case PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_SUCCESS:
      return {
        ...state,
        paymentMethods: Array.isArray(action.payload) ? action.payload : [],
      }
    case PaymentMethodsActionsEnum.GET_PAYMENT_METHODS_FAILURE:
      return { ...state, paymentMethods: [] }
    case PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_ATTEMPT:
      return { ...state, createPaymentMethodStatus: "LOADING" }
    case PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_SUCCESS:
      return { ...state, createPaymentMethodStatus: "SUCCESS" }
    case PaymentMethodsActionsEnum.CREATE_PAYMENT_METHODS_FAILURE:
      return { ...state, createPaymentMethodStatus: "FAILURE" }
    case PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_ATTEMPT:
      return { ...state }
    case PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_SUCCESS:
      return { ...state }
    case PaymentMethodsActionsEnum.DELETE_PAYMENT_METHODS_FAILURE:
      return { ...state }
    default:
      return { ...state }
  }
}

interface PaymentMethod {
  label: string
  description: string
  id: string
}

interface PaymentMethodPayload {
  label: string
  description: string
}

export const getPaymentMethodsThunk =
  (companyId: number) => (dispatch: Dispatch<PaymentMethodsActionsType>) => {
    dispatch(getPaymentMethodsAttemptAction())
    return axios
      .get<Array<PaymentMethod>>(`/payment_methods/get_payment_methods`, {
        params: { company_id: companyId },
      })
      .then((response) =>
        dispatch(getPaymentMethodsSuccessAction(response.data))
      )
      .catch((error) => dispatch(getPaymentMethodsFailureAction(error)))
  }

export const createPaymentMethodsThunk =
  ({
    companyId,
    paymentMethods,
  }: {
    companyId: number
    paymentMethods: PaymentMethodPayload[]
  }) =>
  (dispatch: AppDispatch) => {
    dispatch(createPaymentMethodsAttemptAction())
    return axios
      .post<Array<PaymentMethod>>(`/payment_methods/create_payment_methods`, {
        company_id: companyId,
        payment_methods: paymentMethods,
      })
      .then((response) => {
        dispatch(getPaymentMethodsThunk(companyId))
        dispatch(createPaymentMethodsSuccessAction())
      })
      .catch((error) => dispatch(createPaymentMethodsFailureAction(error)))
  }

export const deletePaymentMethodsThunk =
  ({
    paymentMethodId,
    companyId,
  }: {
    paymentMethodId: string
    companyId: number
  }) =>
  (dispatch: AppDispatch) => {
    dispatch(deletePaymentMethodsAttemptAction())
    return axios
      .delete<Array<PaymentMethod>>(
        `/payment_methods/${paymentMethodId}/delete_payment_method`
      )
      .then((response) => {
        dispatch(getPaymentMethodsThunk(companyId))
        dispatch(deletePaymentMethodsSuccessAction())
      })
      .catch((error) => dispatch(deletePaymentMethodsFailureAction(error)))
  }
